import React from 'react';
import { Breadcrumb } from 'react-bootstrap';
import { Link, useLocation } from 'react-router-dom';
import { FiHome } from 'react-icons/fi';

interface BreadcrumbItem {
  label: string;
  path?: string;
}

const routeLabels: Record<string, string> = {
  admin: 'Admin',
  pessoas: 'Pessoas',
  entidades: 'Entidades',
  operadores: 'Operadores',
  treinamentos: 'Treinamentos',
  aquisicao: 'Aquisição',
  'tipos-madeira': 'Tipos de Madeira',
  certificacoes: 'Certificações',
  'tipos-tratamento': 'Tipos de Tratamento',
  'unidades-medida': 'Unidades de Medida',
  'lotes-madeira': 'Lotes de Madeira',
  'compras-madeira': 'Compras de Madeira',
  'tratamentos-madeira': 'Tratamentos',
  'estoque-madeira': 'Estoque de Madeira',
  estoque: 'Estoque',
  'categorias-produto': 'Categorias',
  produtos: 'Produtos',
  'estoque-produtos': 'Estoque de Produtos',
  armazens: 'Armazéns',
  movimento: 'Movimento',
  'entradas-produtos': 'Entradas de Produtos',
  'saidas-produtos': 'Saídas de Produtos',
  'movimentacoes-produtos': 'Movimentações',
  'pedidos-expedicao': 'Pedidos de Expedição',
  expedicoes: 'Expedições',
  'planejamento-carga': 'Planejamento de Carga',
  producao: 'Produção',
  'tipos-maquina': 'Tipos de Máquina',
  maquinas: 'Máquinas',
  'ordens-producao': 'Ordens de Produção',
  'apontamentos-producao': 'Apontamentos',
  'consumo-madeira': 'Consumo de Madeira',
  'dias-trabalho': 'Dias de Trabalho',
  qualidade: 'Qualidade',
  'inspecoes-pallet': 'Inspeções de Pallet',
  'defeitos-catalogo': 'Catálogo de Defeitos',
  'retrabalhos-pallet': 'Retrabalhos',
  manutencao: 'Manutenção',
  'ordens-manutencao': 'Ordens de Manutenção',
  indicadores: 'Indicadores',
  kpis: 'KPIs',
  'relatorios-produtividade': 'Relatórios de Produtividade',
  custos: 'Custos',
  'custo-padrao-produto': 'Custo Padrão',
  'custo-real-op': 'Custo Real OP',
  'variacao-custo': 'Variação de Custo',
  novo: 'Novo',
  editar: 'Editar',
  relatorio: 'Relatorio',
  etiquetas: 'Etiquetas',
  'etiquetas-modelos': 'Modelos de Etiquetas',
};

export const Breadcrumbs: React.FC = () => {
  const location = useLocation();
  const pathnames = location.pathname.split('/').filter((x) => x);

  if (pathnames.length === 0) {
    return null;
  }

  const normalizedSegments = pathnames[0] === 'admin' ? pathnames.slice(1) : pathnames;
  if (normalizedSegments.length === 0) {
    return null;
  }

  const ACTION_SEGMENTS = ['novo', 'editar', 'relatorio'];
  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  const lastSegment = normalizedSegments[normalizedSegments.length - 1];
  const penultimateSegment = normalizedSegments[normalizedSegments.length - 2];
  const isIdentifier = /^\d+$/.test(lastSegment) || uuidRegex.test(lastSegment);
  const penultimateIsIdentifier =
    /^\d+$/.test(penultimateSegment ?? '') || uuidRegex.test(penultimateSegment ?? '');
  const hasAction = ACTION_SEGMENTS.includes(lastSegment);
  const resourceSegments = hasAction
    ? penultimateIsIdentifier
      ? normalizedSegments.slice(0, -2)
      : normalizedSegments.slice(0, -1)
    : isIdentifier
    ? normalizedSegments.slice(0, -1)
    : normalizedSegments;
  const resourceLabel = routeLabels[resourceSegments[resourceSegments.length - 1]] || resourceSegments[resourceSegments.length - 1];
  const resourcePath = `/admin/${resourceSegments.join('/')}`;
  const finalLabel = hasAction
    ? routeLabels[lastSegment] || (lastSegment === 'editar' ? 'Editar' : lastSegment === 'novo' ? 'Novo' : 'Relatorio')
    : isIdentifier
    ? 'Editar'
    : routeLabels[lastSegment] || lastSegment;
  const items: BreadcrumbItem[] = [];

  if (resourceSegments.length && resourceSegments.join('/') !== normalizedSegments.join('/')) {
    items.push({ label: resourceLabel, path: resourcePath });
  }
  items.push({ label: finalLabel, path: undefined });

  return (
    <Breadcrumb className="mb-4">
      <Breadcrumb.Item 
        linkAs={Link} 
        linkProps={{ 
          to: '/admin', 
          style: { 
            textDecoration: 'none',
            color: 'var(--text)',
            fontSize: '0.9rem'
          } 
        }}
      >
        <FiHome className="me-1" style={{ marginTop: '-2px' }} />
        <span>Início</span>
      </Breadcrumb.Item>
      {items.map((item, index) => (
        <Breadcrumb.Item
          key={index}
          linkAs={item.path ? Link : undefined}
          linkProps={item.path ? { 
            to: item.path, 
            style: { 
              textDecoration: 'none',
              color: 'var(--text)',
              fontSize: '0.9rem'
            } 
          } : undefined}
          active={!item.path}
          style={!item.path ? { 
            fontWeight: 600,
            color: 'var(--primary)',
            fontSize: '0.9rem'
          } : undefined}
        >
          {item.label}
        </Breadcrumb.Item>
      ))}
    </Breadcrumb>
  );
};

